import type { AssistanceBenefits } from './types/assistance.types';

/**
 * Nom commercial de l'offre, tel qu'il doit apparaître dans l'interface.
 *
 * Une seule constante, parce que trois écrans l'écrivaient différemment :
 * « Assistance », « Assist », « Affinicar Assist ». Le badge court de la LISTE
 * DES CONTRATS fait exception et garde son libellé « Assist » : la colonne est
 * étroite et ce badge est déjà installé.
 */
export const ASSISTANCE_LABEL = 'Affinicar Assist';

/**
 * Formulation commerciale d'une formule d'assistance, telle qu'elle est présentée
 * au client sur le contrat.
 *
 * Composée depuis le SNAPSHOT du contrat (`assistanceBenefits`, qui porte `tier` et
 * `loanerDays` figés à la souscription) et non depuis le référentiel : renommer une
 * formule ne doit pas réécrire ce qui est affiché sur un contrat déjà signé.
 *
 * Distincte d'`assistanceLabel`, qui reste le libellé COURT — celui de la ligne de
 * facture (« GARANTIE PREMIUM 24 (Assistance Premium — 15 jours) »), où la
 * formulation longue serait illisible et ferait disparaître le mot « Assistance ».
 *
 * Repli sur `label` pour les contrats souscrits avant l'enrichissement du snapshot :
 * leur `assistanceBenefits` ne porte ni `tier` ni `loanerDays`.
 */
export function assistanceFormulaWording(
  benefits: (AssistanceBenefits & { tier?: string; loanerDays?: number }) | Record<string, unknown> | null | undefined,
  label?: string | null,
): string | null {
  const tier = benefits && typeof benefits === 'object' ? (benefits as Record<string, unknown>).tier : undefined;
  const loanerDays = benefits && typeof benefits === 'object' ? (benefits as Record<string, unknown>).loanerDays : undefined;

  if (tier === 'standard') return 'Formule Standard';
  if (tier === 'premium' && typeof loanerDays === 'number' && loanerDays > 0) {
    return `Formule Premium — Véhicule de prêt ${loanerDays} jours`;
  }

  return label ?? null;
}

/** Phrase complète affichée sur le contrat et à la confirmation de souscription. */
export function assistanceSentence(
  benefits: Record<string, unknown> | null | undefined,
  label?: string | null,
): string | null {
  const wording = assistanceFormulaWording(benefits, label);
  return wording ? `Ce contrat dispose du service Affinicar Assist — ${wording}.` : null;
}

/** Ce qui a déjà été transmis à l'assisteur pour un contrat donné. */
export interface AssistanceCancellationImpact {
  /** Une ligne du flux assurés est partie : Mutuaide a cet assuré en portefeuille. */
  notifiedToInsurer: boolean;
  /** Une déclaration de prime est partie pour ce contrat. */
  premiumDeclared: boolean;
}

/**
 * Ce qu'une annulation de contrat entraînera chez l'assisteur.
 *
 * Deux choses s'y jouent, et elles ne suivent pas la même règle :
 *
 *  - LA FIN DE DROIT part toujours, dès lors que l'assisteur connaît le contrat.
 *    Un bénéficiaire dont le contrat est annulé ne doit jamais rester assistable
 *    au portefeuille de Mutuaide.
 *  - LA PRIME ne se réclame que si le vendeur ne paiera pas la prestation :
 *    contrat jamais facturé, ou avoir rendu. Facturé sans avoir, elle nous reste
 *    due et rien n'est déclaré. Miroir de `resolveCancelInsurer` côté API — toute
 *    modification de la règle doit toucher les deux.
 *
 * D'où `premiumClaimed`, que l'appelant calcule depuis la réponse à la question de
 * l'avoir : la phrase suit la case en direct, plutôt que d'énumérer les issues.
 *
 * Rend `null` tant que l'état n'est pas connu : mieux vaut ne rien promettre que
 * promettre la mauvaise chose pendant le chargement.
 *
 * « Ce soir » et non « à 23 h » : le différentiel a cette heure par défaut, mais
 * `MUTUAIDE_EXPORT_CRON` peut la déplacer, et la phrase deviendrait fausse.
 */
export function assistanceCancellationNotice(
  impact: AssistanceCancellationImpact | undefined | null,
  premiumClaimed: boolean,
): string | null {
  if (!impact) return null;

  if (!impact.notifiedToInsurer) {
    return "L'assisteur n'a jamais reçu ce contrat : rien ne lui sera déclaré ni annulé.";
  }

  const finDeDroit = "La fin de droit sera transmise à l'assisteur ce soir.";

  if (!premiumClaimed) {
    return (
      `${finDeDroit} La prime lui reste due : sans avoir au vendeur, ` +
      "aucune annulation de prime ne sera déclarée."
    );
  }

  // Compensée si la déclaration n'est pas encore partie, remboursée le mois
  // suivant si elle l'est déjà.
  return impact.premiumDeclared
    ? `${finDeDroit} Une annulation de prime sera déclarée le 7 du mois prochain.`
    : `${finDeDroit} La prime ne sera pas facturée : sa création et son annulation se compensent dans la déclaration du mois.`;
}

/**
 * Encart des conditions particulières, prêt à afficher.
 *
 * Deux phrases, sans détail des prestations : celui-ci reste au paramétrage de la
 * garantie, et figé sur le contrat pour la valeur de preuve.
 */
export interface AssistanceEncart {
  title: string;
  /** Phrase d'introduction, `{formule}` déjà substitué. */
  intro: string;
  /** Mention de bas d'encart, `{numero}` et `{email}` déjà substitués. */
  footer: string | null;
}

/**
 * Compose l'encart d'assistance des CP, ou `null` s'il ne doit pas apparaître.
 *
 * Miroir de `AssistanceArticleService.renderCpEncart` côté API : l'aperçu du
 * wizard et le PDF définitif lisent le même article et appliquent les mêmes
 * règles, pour ne pas montrer au vendeur autre chose que ce que le client recevra.
 *
 * Les mêmes trois motifs d'absence : pas d'assistance sur le contrat, article
 * inexistant (migration non jouée), ou article masqué.
 */
export function buildAssistanceEncart(
  article: {
    title: string;
    intro: string;
    footer: string | null;
    phone: string | null;
    email: string | null;
    isVisible: boolean;
  } | null | undefined,
  benefits: Record<string, unknown> | null | undefined,
  label?: string | null,
): AssistanceEncart | null {
  if (!article || !article.isVisible) return null;

  const wording = assistanceFormulaWording(benefits, label);
  if (!wording) return null;

  return {
    title: article.title,
    intro: article.intro.replace('{formule}', wording),
    footer: article.footer
      ? article.footer
        .replace('{numero}', article.phone ?? '')
        .replace('{email}', article.email ?? '')
      : null,
  };
}
